import React, {useContext, useState} from "react";
import {useDndMonitor} from "@dnd-kit/core";
import {useSortable} from "@dnd-kit/sortable";
import {GridRow, GridRowProps} from "@mui/x-data-grid";
import {Box} from "@bounce/shared/ui";
import {DndEnabledContext, RowHandleContext, getSortableRowStyle} from "../../components/sortable";
import {CategoryHeader} from "./CategoryHeader";
import {CategoryDragContext, CategoryHeaderContext, SegmentTypeSeparatorContext} from "./SegmentDndContext";
import {CATEGORY_BLOCK_SPACING} from "./segmentCategoryBlocks";
import {SEGMENT_TYPE_SEPARATOR_HEIGHT, SegmentTypeSeparator} from "./SegmentTypeSeparator";

export const SortableSegmentRow = (props: GridRowProps) => {
  const enabled = useContext(DndEnabledContext);
  const {firstGeneralId, isBlocked} = useContext(SegmentTypeSeparatorContext);
  const {draggedId, draggedCategory} = useContext(CategoryDragContext);
  const categoryHeader = useContext(CategoryHeaderContext).get(Number(props.rowId));
  const [followOffsetY, setFollowOffsetY] = useState(0);
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: props.rowId,
    disabled: !enabled,
  });

  const followsDraggedRow =
    draggedCategory !== undefined && props.rowId !== draggedId && props.row.segment_category === draggedCategory;

  useDndMonitor({
    onDragStart: () => setFollowOffsetY(0),
    onDragMove: ({delta}) => {
      if (followsDraggedRow) setFollowOffsetY(delta.y);
    },
  });

  const style = followsDraggedRow
    ? getSortableRowStyle(props.style, {
        transform: {x: 0, y: followOffsetY, scaleX: 1, scaleY: 1},
        transition: undefined,
        isDragging: true,
      })
    : getSortableRowStyle(props.style, {transform, transition, isDragging});

  const row = (
    <RowHandleContext.Provider value={{attributes, listeners}}>
      <GridRow ref={setNodeRef} {...props} style={style} />
    </RowHandleContext.Provider>
  );

  const showSeparator = props.rowId === firstGeneralId;
  if (!showSeparator && !categoryHeader) return row;

  return (
    <Box role="presentation" sx={{position: "relative", display: "flow-root"}}>
      {showSeparator && <SegmentTypeSeparator isBlocked={isBlocked} />}
      {categoryHeader && (
        <RowHandleContext.Provider value={{attributes, listeners}}>
          <CategoryHeader
            {...categoryHeader}
            top={(showSeparator ? SEGMENT_TYPE_SEPARATOR_HEIGHT : 0) + CATEGORY_BLOCK_SPACING}
            style={{transform: style.transform, transition: style.transition}}
          />
        </RowHandleContext.Provider>
      )}
      {row}
    </Box>
  );
};
